<template>
  <sidebar
    :drawer-open="isOpen"
    :cancel-button="cancelButtonLabel"
    :permanent="isOpen"
    should-prevent-close-conditional
    :actions="false"
    class="movable-filter-sidebar"
    @closeSidebar="onSidebarClose"
    @clickOutside="onSidebarClose"
  >
    <template #titleRegion>
      <div class="title-region">
        <span>{{ title }}</span>
        <button-common
          v-if="clearButtonLabel"
          :disabled="!isEnabledClearFilters"
          type="clear"
          color="accent"
          @click="onClearButtonClick"
        >
          {{ clearButtonLabel }}
        </button-common>
      </div>
    </template>
    <template #tabContent>
      <v-card flat>
        <v-card-text>
          <draggable-component-list
            v-if="!loading"
            :value="filterList"
            @input="onListOrderUpdate"
          />
          <skeleton
            v-else
            type="text"
          />
        </v-card-text>
      </v-card>
    </template>
    <template #bottomContent>
      <div
        v-if="switchButtonLabel"
        class="bottom-content"
      >
        <span class="switch-button-label">{{ switchButtonLabel }}</span>
        <switch-button
          :value="isTogglerEnabled"
          @input="onToggleSwitch"
        />
      </div>
    </template>
  </sidebar>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import buttonCommon from '../../buttons/ButtonCommon.vue';
import DraggableComponentList, { ListItem } from '../../DragAndDrop/DraggableComponentList.vue';
import skeleton from '../../loaders/SidebarSkeleton.vue';
import switchButton from '../../selection-controls/SwitchButton.vue';
import sidebar from '../SideBar/SideBar.vue';

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'clear'): void;
  (e: 'update', list: ListItem[]): void;
  (e: 'toggle', value: boolean, list: ListItem[]): void;
}>();

const props = withDefaults(
  defineProps<{
    elementList: ListItem[];
    cancelButtonLabel?: string;
    clearButtonLabel?: string;
    initialFilterOrder?: string[];
    saveButtonLabel?: string;
    switchButtonLabel?: string;
    switchValue?: boolean;
    title?: string;
    isOpen?: boolean;
  }>(),
  {
    cancelButtonLabel: 'Cancel',
    clearButtonLabel: '',
    initialFilterOrder: () => [],
    saveButtonLabel: 'Save changes',
    switchButtonLabel: '',
    switchValue: false,
    title: '',
    isOpen: false,
  }
);

const loading = ref(false);
const list = ref<ListItem[]>([]);

const filterList = computed({
  get: () => (list.value?.length ? list.value : props.elementList),
  set: (newValue) => {
    list.value = newValue;
  },
});

const activeFirst = ref<boolean>();
const isTogglerEnabled = computed({
  get: () => activeFirst.value ?? props.switchValue,
  set: (newValue) => {
    activeFirst.value = newValue;
  },
});

// Update the order when the filter list changes and the isTogglerEnabled is true.
watch(
  () => props.elementList,
  (newValue) => {
    if (isTogglerEnabled.value) {
      emit('update', newValue);
    }
  },
  { deep: true }
);

const checkIfValueExists = (value: string | number | object | null | boolean | undefined) => {
  if ([undefined, null].includes(value)) return false;
  if (Array.isArray(value)) return value.length;
  if (typeof value === 'object') return Object.keys(value).length;

  return value;
};

const onListOrderUpdate = (list: ListItem[]) => {
  if (!isTogglerEnabled.value) {
    filterList.value = list;
  }

  emit('update', list);
};

const isEnabledClearFilters = computed(() => filterList.value?.some((item) => checkIfValueExists(item.props?.value)));

const onToggleSwitch = async (value: boolean) => {
  let updatedList = [...filterList.value];

  if (!value && !props.switchButtonLabel && props.initialFilterOrder) {
    updatedList = props.initialFilterOrder.map((filter) =>
      updatedList?.find((filterItem) => filterItem.value === filter)
    );
  }

  emit('toggle', value, updatedList);
};

onMounted(() => {
  if (props.switchButtonLabel && props.switchValue) {
    const updatedList = filterList.value;
    emit('update', updatedList);
  }
});

const onSidebarClose = async () => {
  emit('close');
};

const onClearButtonClick = async () => {
  emit('clear');
};

onUnmounted(() => {
  clearOrderedFiltersList();
});

const clearOrderedFiltersList = () => {
  filterList.value = [];
};
</script>

<style lang="scss" scoped>
@import '../../../assets/styles/main';

.movable-filter-sidebar {
  z-index: 15 !important;

  &__text {
    @include subtitle-2;
    color: $primary;
    margin: 0 0 20px 10px;
  }

  .hr {
    height: 1px;
    border: none;
    background-color: $elements;
    margin: 32px 0 24px 0;
  }

  .title-region {
    font-size: 14px;
    height: 50px;
    width: 90%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.bottom-content {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 20px 40px;
  border-top: 1px solid $elements;

  .switch-button-label {
    font-size: 14px;
    font-weight: 500;
  }
}
</style>
